import { Injectable, Injector } from '@angular/core';

import {
  EventSetupState,
  EventStatus,
} from '@deltager/organizers/admin/modules/events/create-event/store/event-setup.state';

import { EventsService } from '@deltager/organizers/admin/core/services/events.service';
import { UserService } from '@deltager/organizers/admin/core/services/user.service';
import { Event } from '@deltager/organizers/admin/modules/events/create-event/model/event.model';

import { catchError, concatMap, finalize, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { forkJoin, of, from, timer, merge } from 'rxjs';
import { SpinnerOverlayService } from '@deltager/organizers/admin/shared/spinner-overlay/spinner-overlay.service';
import { OrganizersService } from '@deltager/organizers/admin/core/services/organizers.service';
import { EventType } from '@deltager/organizers/admin/shared/models/EventType';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ChangeEventStatusAction,
  ChangeEventStepAction,
  ConstructEventRequestAction,
  ConstructEventSuccessAction,
  CreateEventAndAddSessionsRequestAction,
  CreateEventRequestAction,
  CreateEventSuccessAction,
  EventActionTypes,
  EventErrorAction,
  GeneralEventUpdateErrorAction,
  LoadEventRequestAction,
  LoadEventSuccessAction,
  PublishEventRequestAction,
  PublishEventSuccessAction,
  UpdateEventRequestAction,
  UpdateEventSuccessAction,
} from './event-setup.actions';
import { ShowNotificationAction } from './event-setup.ui.actions';
import { AppNotificationType } from '@deltager/organizers/admin/shared/models/AppNotificationType';
import { Action, Store } from '@ngrx/store';
import { FotoWebService } from '../shared/services/fotoweb.service';
import { StorageService } from '@deltager/organizers/admin/core/services/storage.service';
import {
  EventSetupError,
  EventSetupErrorType,
} from '@deltager/organizers/admin/modules/events/create-event/model/event-error.model';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { SaveChangesSource, selectOrganizer } from '@deltager/organizers/admin/redux/root.state';
import { AppNotificationsService } from '@deltager/organizers/admin/core/services/app-notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { SaveSessionsAction } from '@deltager/organizers/admin/modules/event-sessions/store/sessions.actions';
import { EventSetupWizardStep } from '@deltager/organizers/admin/modules/events/create-event/model/event-setup-wizard-step.enum';
import { PaymentServiceProviderService } from '../../../payment-service-provider/payment-service-provider.service';
import _ from 'lodash';
import { OrganizerSettings } from '@deltager/organizers/admin/shared/models/OrganizerSettings';

@Injectable()
export class EventSetupEffects {
  constructor(
    private actions$: Actions,
    private eventService: EventsService,
    private userService: UserService,
    private spinner: SpinnerOverlayService,
    private organizersService: OrganizersService,
    private router: Router,
    private store: Store<{ event: EventSetupState; organizer: OrganizerSettings }>,
    private fotoWebService: FotoWebService,
    private paymentServiceProviderService: PaymentServiceProviderService,
    private storageService: StorageService,
    private notificationService: AppNotificationsService,
    private injector: Injector,
  ) {}

  getEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActionTypes.LoadEventRequest),
      mergeMap((action: LoadEventRequestAction) => {
        const eventId = action.payload.eventId;

        this.spinner.show();
        let eventObserver = this.organizersService.getEventById(this.userService.OrganizerId, eventId);
        return eventObserver.pipe(
          concatMap(eventResult => {
            const parentId = eventResult.parentId;
            if (parentId > 0) {
              this.router.navigate([`/events/edit/${parentId}`]);
              let eventObserver = this.organizersService.getEventById(this.userService.OrganizerId, parentId);
              let headerObserver = this.eventService.getEventHeader(parentId);
              return forkJoin([eventObserver, headerObserver]).pipe(
                map(result => {
                  return new LoadEventSuccessAction({ event: result[0], eventStats: result[1] });
                }),
              );
            }
            let headerObserver = this.eventService.getEventHeader(eventId);
            return headerObserver.pipe(
              map(headerResult => {
                return new LoadEventSuccessAction({ event: eventResult, eventStats: headerResult });
              }),
            );
          }),
          catchError((err: HttpErrorResponse) => {
            if (err.status === HttpStatusCode.NotFound) {
              const translateService = this.injector.get(TranslateService);

              this.notificationService.sendError({
                message: translateService.instant('validation.event_not_found'),
              });
              return timer(3000).pipe(
                switchMap(() => {
                  this.router.navigate(['page-not-found'], { skipLocationChange: true });
                  return of(this.createErrorMessageFromHttpResponse(err));
                }),
              );
            }
            return of(this.createErrorMessageFromHttpResponse(err));
          }),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });

  constructEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActionTypes.ConstructEventRequest),
      mergeMap((action: ConstructEventRequestAction) => {
        if (action.payload.preferCached) {
          const storedData = this.storageService.getItemFromSession<Event>('cachedInitialEvent', false);
          if (storedData) {
            return of(storedData).pipe(map(() => new ConstructEventSuccessAction({ event: storedData })));
          }
        }

        this.spinner.show();

        // Get organizer settings from store
        const organizerSettings$ = this.store.select(selectOrganizer).pipe(take(1));
        const userObserver = from(this.userService.getUserData());

        const clubId = this.userService.OrganizerId;

        const templateObserver = this.eventService.getTemplateList(clubId, true);

        const wizardId = action.payload.wizardId ?? 0;
        const eventType = action.payload.eventType ?? EventType.Regular;
        const wizardObserver = this.eventService.getWizardStep1(wizardId);

        return forkJoin([userObserver, templateObserver, wizardObserver, organizerSettings$]).pipe(
          map(results => {
            const event = Event.BuildEventModel(
              results[0].user.fullName,
              results[0].user.mobile,
              results[0].user.email,
              results[3].orgNumber,
              results[3].personNumber,
              results[1][0].id,
              results[2],
              eventType,
            );

            return new ConstructEventSuccessAction({ event: event });
          }),
          catchError(err => of(this.createErrorMessageFromHttpResponse(err))),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });

  createEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActionTypes.CreateEventRequest),
      mergeMap((action: CreateEventRequestAction) => {
        this.spinner.show();

        const newEvent = _.cloneDeep(action.payload.event);
        newEvent.organizerId = this.userService.OrganizerId;

        const notificationMessage = action.payload.notificationMessage;
        const wizardId = action.payload.wizardId ?? 0;
        const source = action.payload.source;

        let observable = this.eventService.createEvent(newEvent, wizardId);

        switch (source) {
          case SaveChangesSource.fotoWeb:
            return observable.pipe(
              concatMap(event =>
                from(
                  this.fotoWebService.authenticate(`${window.location.href.split('#')[0]}#/events/edit/${event.id}`),
                ).pipe(
                  take(1),
                  map(_ => newEvent),
                ),
              ),
              switchMap(_ => []),
              finalize(() => this.spinner.hide()),
            );
          default:
            return observable.pipe(
              switchMap(event => [
                new CreateEventSuccessAction({ event: event }),
                new ChangeEventStatusAction({ status: EventStatus.created }),
                new ShowNotificationAction({
                  message: notificationMessage,
                  notificationType: AppNotificationType.SUCCESS,
                }),
              ]),
              catchError(err =>
                merge(of(new GeneralEventUpdateErrorAction()), of(this.createErrorMessageFromHttpResponse(err))),
              ),
              finalize(() => this.spinner.hide()),
            );
        }
      }),
    );
  });

  createEventAndSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActionTypes.CreateEventAndAddSessionsRequest),
      mergeMap((action: CreateEventAndAddSessionsRequestAction) => {
        this.spinner.show();

        const newEvent = {
          ...action.payload.event,
          organizerId: this.userService.OrganizerId,
        } as Event;

        const notificationMessage = action.payload.notificationMessage;
        const wizardId = action.payload.wizardId ?? 0;

        let observable = this.eventService.createEvent(newEvent, wizardId);

        return observable.pipe(
          switchMap(event => [
            new SaveSessionsAction({
              ...action.payload.sessionPayload,
              eventId: event.id,
            }),
            new CreateEventSuccessAction({ event: event }),
            new ChangeEventStatusAction({ status: EventStatus.createdButWithSessions }),
            new ShowNotificationAction({
              message: notificationMessage,
              notificationType: AppNotificationType.SUCCESS,
            }),
            new ChangeEventStepAction({ step: EventSetupWizardStep.EventDetails }),
          ]),
          catchError(err =>
            merge(of(new GeneralEventUpdateErrorAction()), of(this.createErrorMessageFromHttpResponse(err))),
          ),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });

  private onboardReturnUrl(eventId: number): string {
    let url = window.location.href;
    const split = url.split('#');
    return `${split[0]}#/post-onboarding?eventId=${eventId}&eventType=${EventType.Regular}`;
  }

  updateEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActionTypes.UpdateEventRequest),
      mergeMap((action: UpdateEventRequestAction) => {
        this.spinner.show();

        const { event, eventOperations, notificationMessage, source } = action.payload;

        let updateEvent$ = this.eventService.saveEvent(event, eventOperations);

        switch (source) {
          case SaveChangesSource.fotoWeb:
            return updateEvent$.pipe(
              concatMap(event =>
                from(
                  this.fotoWebService.authenticate(`${window.location.href.split('#')[0]}#/events/edit/${event.id}`),
                ).pipe(
                  take(1),
                  map(_ => event),
                  switchMap(() => []),
                  finalize(() => this.spinner.hide()),
                ),
              ),
            );
          case SaveChangesSource.onboard:
            return updateEvent$.pipe(
              concatMap(() =>
                from(
                  this.paymentServiceProviderService.onboard(
                    event.id,
                    event.eventType,
                    this.onboardReturnUrl(event.id),
                  ),
                ).pipe(
                  take(1),
                  map(_ => event),
                  switchMap(() => []),
                  finalize(() => this.spinner.hide()),
                ),
              ),
            );
          default:
            return updateEvent$.pipe(
              withLatestFrom(this.store.select(s => s.event.currentEventStatus)),
              switchMap(([event, currentEventStatus]) => {
                event.isPriceBreakDownEnabled = event.isPriceBreakDownEnabled;

                const todoActions: Action[] = [new UpdateEventSuccessAction({ event: event })];

                if (currentEventStatus === EventStatus.updating) {
                  todoActions.push(new ChangeEventStatusAction({ status: EventStatus.updated }));
                } else if (currentEventStatus === EventStatus.submitting) {
                  todoActions.push(new ChangeEventStatusAction({ status: EventStatus.submitted }));
                }

                todoActions.push(
                  new ShowNotificationAction({
                    message: notificationMessage,
                    notificationType: AppNotificationType.SUCCESS,
                  }),
                );

                return todoActions;
              }),
              catchError(err =>
                merge(of(new GeneralEventUpdateErrorAction()), of(this.createErrorMessageFromHttpResponse(err))),
              ),
              finalize(() => this.spinner.hide()),
            );
        }
      }),
    );
  });

  publishEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActionTypes.PublishEventRequest),
      mergeMap((action: PublishEventRequestAction) => {
        this.spinner.show();

        const eventToPublish = action.payload.event;
        const eventOperations = action.payload.eventOperations;
        const notificationMessage = action.payload.notificationMessage;

        return this.eventService.saveEvent(eventToPublish, eventOperations).pipe(
          mergeMap(() => this.eventService.publishEvent(eventToPublish.id)),
          switchMap(event => [
            new PublishEventSuccessAction({ event: event }),
            new ShowNotificationAction({ message: notificationMessage, notificationType: AppNotificationType.SUCCESS }),
          ]),
          catchError(err =>
            merge(of(new GeneralEventUpdateErrorAction()), of(this.createErrorMessageFromHttpResponse(err))),
          ),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });

  createErrorMessageFromHttpResponse(httpError: HttpErrorResponse): EventErrorAction {
    if (!httpError)
      return new EventErrorAction({ message: 'Unknown error', type: EventSetupErrorType.generic } as EventSetupError);

    return new EventErrorAction({
      message:
        httpError.status !== HttpStatusCode.NotFound
          ? (httpError.error?.message ?? httpError.message)
          : 'Resource Not Found',
      type:
        httpError.status === HttpStatusCode.Forbidden
          ? EventSetupErrorType.permissionDenied
          : EventSetupErrorType.generic,
    });
  }
}
